// ================================================================
// 問題瀏覽頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：瀏覽、搜尋、篩選問題，選擇問題進行查詢
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  getQuestions,
  getPopularQuestions,
  searchQuestions,
  getCategoryColor,
  getCategoryName,
  getDynamicQuestionTitle,
  Question,
  QuestionCategory
} from '../api/questions';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * 檢查問題是否免費（處理數字/布爾值）
 */
const isQuestionFree = (isFree: boolean | number | undefined): boolean => {
  if (typeof isFree === 'boolean') return isFree;
  if (typeof isFree === 'number') return isFree === 1;
  return false;
};

/**
 * 問題瀏覽頁面組件
 */
const QuestionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatPrice } = useCurrency();

  // 狀態
  const [questions, setQuestions] = useState<Question[]>([]);
  const [filteredQuestions, setFilteredQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // 篩選與搜尋（從 URL 取得初始類別，避免 race condition）
  const [selectedCategory, setSelectedCategory] = useState<string>(
    () => searchParams.get('category') || 'all'
  );
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [showPopular, setShowPopular] = useState<boolean>(false);

  // 類別列表（排序：完整分析、配對分析、自我分析、常見問題、全部問題）
  const categories = [
    { id: 'FULL', name: '完整分析', color: '#9c27b0' },
    { id: 'PAIR', name: '配對分析', color: '#e91e63' },
    { id: 'MAIN', name: '自我分析', color: '#ff9800' },
    { id: 'COMMON', name: '常見問題', color: '#2196f3' },
    { id: 'SIMPLE', name: '優惠咨詢', color: '#4caf50' },
    { id: 'all', name: '全部問題', color: '#9e9e9e' }
  ];

  // 載入問題
  const loadQuestions = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      let result;

      if (showPopular) {
        // 熱門問題
        result = await getPopularQuestions(20);
      } else if (selectedCategory === 'all') {
        // 所有問題
        result = await getQuestions();
      } else {
        // 特定類別
        result = await getQuestions(selectedCategory as QuestionCategory);
      }

      if (result.success) {
        // 過濾掉 SIMPLE002 (每日幸運元素，在 Dashboard 自動顯示)
        const availableQuestions = result.questions.filter(
          (q: Question) => q.question_id !== 'SIMPLE002'
        );
        setQuestions(availableQuestions);
        setFilteredQuestions(availableQuestions);
      } else {
        setError('載入問題失敗');
      }
    } catch (err: any) {
      setError(err.message || '載入問題失敗');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showPopular]);

  // 載入問題列表
  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  // 處理搜尋
  const handleSearch = async () => {
    if (!searchKeyword.trim()) {
      setFilteredQuestions(questions);
      return;
    }

    setLoading(true);

    try {
      const result = await searchQuestions(searchKeyword);

      if (result.success) {
        // 過濾掉 SIMPLE002
        const availableQuestions = result.questions.filter(
          (q: Question) => q.question_id !== 'SIMPLE002'
        );
        setFilteredQuestions(availableQuestions);
      } else {
        setError('搜尋失敗');
      }
    } catch (err: any) {
      setError(err.message || '搜尋失敗');
    } finally {
      setLoading(false);
    }
  };

  // 處理類別切換
  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedCategory(newValue);
    setSearchParams({ category: newValue });
    setSearchKeyword('');
    setShowPopular(false);
  };

  // 前往問題詳情
  const goToQuestionDetail = (questionId: string) => {
    navigate(`/questions/${questionId}`);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* 頁面標題 */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#667eea' }}>
        問題瀏覽
      </Typography>

      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        選擇您感興趣的問題，立即開始查詢
      </Typography>

      {/* 搜尋框 */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="搜尋問題關鍵字..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <Button onClick={handleSearch} variant="contained" size="small">
                搜尋
              </Button>
            )
          }}
        />
      </Box>

      {/* 快捷按鈕 */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button
          variant={showPopular ? 'contained' : 'outlined'}
          startIcon={<TrendingUpIcon />}
          onClick={() => {
            setShowPopular(!showPopular);
            setSelectedCategory('all');
          }}
        >
          熱門問題
        </Button>

        <Button
          variant="outlined"
          startIcon={<LocalOfferIcon />}
          onClick={() => {
            setSelectedCategory('SIMPLE');
            setShowPopular(false);
          }}
        >
          優惠咨詢
        </Button>
      </Box>

      {/* 類別標籤 */}
      <Tabs
        value={selectedCategory}
        onChange={handleCategoryChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {categories.map((cat) => (
          <Tab
            key={cat.id}
            label={cat.name}
            value={cat.id}
            sx={{ fontWeight: selectedCategory === cat.id ? 'bold' : 'normal' }}
          />
        ))}
      </Tabs>

      {/* 錯誤訊息 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 載入中 */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* 問題列表 */}
      {!loading && filteredQuestions.length === 0 && (
        <Alert severity="info">
          沒有找到符合條件的問題
        </Alert>
      )}

      {!loading && filteredQuestions.length > 0 && (
        <Grid container spacing={2}>
          {filteredQuestions.map((question) => (
            <Grid item xs={12} sm={6} md={4} key={question.question_id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* 問題類別（中文） */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                    <Chip
                      label={getCategoryName(question.category_id)}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(question.category_id),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>

                  {/* 問題文字 */}
                  <Typography variant="body1" gutterBottom sx={{ fontWeight: 500, minHeight: 48 }}>
                    {getDynamicQuestionTitle(question)}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  {/* 價格 */}
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {isQuestionFree(question.is_free) ? '免費' : formatPrice(question.price)}
                  </Typography>

                  {/* 查看按鈕 */}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => goToQuestionDetail(question.question_id)}
                  >
                    查看詳情
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* 顯示結果數量 */}
      {!loading && filteredQuestions.length > 0 && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            共顯示 {filteredQuestions.length} 個問題
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default QuestionsPage;
